import { useState } from 'react';

import { Link, useNavigate } from 'react-router-dom';

import PropTypes from 'prop-types';
import { BsCheck2, BsEye, BsEyeSlash } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';
import Checkbox from '#globalComponents/Checkbox/Checkbox';
import TextInput from '#globalComponents/Forms/TextInput';
import { useAuth } from '#globalContexts/AuthProvider';
import { generateEventId, trackLead } from '#lib/facebookConversionApi';
import { isValidEmail } from '#lib/textUtils';

import registerBackground from '../../../../assets/images/register-background.jpg';
import { getRegistrationErrorMessage } from '../../utils/errorMessages';

const ProgressBar = ({ step }) => (
	<div className="h-1.5 w-full bg-gray-100 rounded-full mb-6 overflow-hidden">
		<div
			className="h-full bg-gradient-to-r from-primary-500 to-primary-600 rounded-full transition-all duration-300"
			style={{ width: step === 1 ? '50%' : '100%' }}
		/>
	</div>
);

ProgressBar.propTypes = {
	step: PropTypes.number.isRequired,
};

const LabeledInput = ({
	id,
	label,
	required = false,
	hint = null,
	rightSlot = null,
	className = '',
	...inputProps
}) => (
	<div className={className}>
		<label htmlFor={id} className="block text-sm font-medium text-text mb-1.5">
			{label}
			{required && <span className="text-warning-500"> *</span>}
		</label>
		<div className="relative">
			<TextInput id={id} required={required} className={rightSlot ? 'pr-12' : ''} {...inputProps} />
			{rightSlot}
		</div>
		{hint && <p className="mt-1.5 text-xs text-bgBase-800">{hint}</p>}
	</div>
);

LabeledInput.propTypes = {
	id: PropTypes.string.isRequired,
	label: PropTypes.string.isRequired,
	required: PropTypes.bool,
	hint: PropTypes.string,
	rightSlot: PropTypes.node,
	className: PropTypes.string,
};

const RegisterPage = () => {
	const [step, setStep] = useState(1);
	const [formData, setFormData] = useState({
		email: '',
		password: '',
		firstName: '',
		lastName: '',
		companyName: '',
		acceptedTerms: false,
	});
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState('');
	const [success, setSuccess] = useState('');
	const [showLoginOption, setShowLoginOption] = useState(false);
	const [showPassword, setShowPassword] = useState(false);

	const { signUp } = useAuth();
	const navigate = useNavigate();

	const handleChange = (e) => {
		const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
		setFormData((prev) => ({
			...prev,
			[e.target.name]: value,
		}));
	};

	const handleEmailSubmit = (e) => {
		e.preventDefault();
		setError('');

		if (!formData.email) {
			setError('Bitte geben Sie eine E-Mail-Adresse ein');
			return;
		}

		if (!isValidEmail(formData.email)) {
			setError('Bitte geben Sie eine gültige E-Mail-Adresse ein');
			return;
		}

		if (!formData.acceptedTerms) {
			setError('Bitte akzeptieren Sie die Nutzungsbedingungen und Datenschutzerklärung');
			return;
		}

		// Fire a Lead event (intent signal) the moment a valid email is captured
		const leadEventId = generateEventId();
		if (typeof window.fbq === 'function') {
			try {
				window.fbq('track', 'Lead', {}, { eventID: leadEventId });
			} catch {
				// ignored
			}
		}
		void trackLead(formData.email, leadEventId);

		setStep(2);
	};

	const handleCompleteRegistration = async (e) => {
		e.preventDefault();

		//Catch multiple clicks on registration button to prevent duplicates
		if (loading) return;

		setError('');
		setSuccess('');
		setShowLoginOption(false);

		// Validierung
		if (formData.password.length < 6) {
			setError('Passwort muss mindestens 6 Zeichen lang sein');
			return;
		}

		setLoading(true);

		try {
			const { error } = await signUp(formData.email, formData.password, {
				firstName: formData.firstName,
				lastName: formData.lastName,
				companyName: formData.companyName,
			});

			if (error) {
				const { errorMessage } = getRegistrationErrorMessage(error);
				setError(errorMessage);
				// Better Auth reports duplicates as "User already exists."
				if (/already exists|already registered/i.test(error.message ?? '')) {
					setShowLoginOption(true);
				}
			} else {
				setSuccess('Registrierung erfolgreich! Bitte überprüfen Sie Ihre E-Mails zur Bestätigung.');
			}
		} catch (err) {
			setError('Ein unerwarteter Fehler ist aufgetreten');
		} finally {
			setLoading(false);
		}
	};

	const renderStep1 = () => (
		<Card className="p-8 max-w-lg mx-4">
			<ProgressBar step={1} />

			<div className="text-left mb-6">
				<h1 className="text-lg/8 font-bold text-text mb-2">Starte noch heute kostenlos</h1>
				<p className="text-text">In wenigen Sekunden zur ersten Lead-Generierung</p>
			</div>

			<div className="mb-6">
				<ul className="space-y-2 text-sm text-text">
					<li className="flex items-center">
						<BsCheck2 className="w-5 h-5 text-success-500 mr-2 flex-shrink-0" />
						Testen Sie 50 Kontakte kostenlos
					</li>
					<li className="flex items-center">
						<BsCheck2 className="w-5 h-5 text-success-500 mr-2 flex-shrink-0" />
						Keine Kreditkarte erforderlich
					</li>
					<li className="flex items-center">
						<BsCheck2 className="w-5 h-5 text-success-500 mr-2 flex-shrink-0" />
						Persönlicher Support
					</li>
				</ul>
			</div>

			{error && (
				<div className="bg-red-50 border border-red-200 text-warning-700 px-4 py-3 rounded-lg mb-4">
					{error}
				</div>
			)}

			<form onSubmit={handleEmailSubmit}>
				<LabeledInput
					id="email"
					label="E-Mail-Adresse"
					required
					type="email"
					name="email"
					autoComplete="email"
					placeholder="max@muster-gmbh.de"
					value={formData.email}
					onChange={handleChange}
					className="mb-4"
				/>

				<div className="mb-4">
					<Checkbox
						id="acceptedTerms"
						name="acceptedTerms"
						checked={formData.acceptedTerms}
						onChange={(checked) => setFormData((prev) => ({ ...prev, acceptedTerms: checked }))}
						required
						label={
							<span className="text-xs text-bgBase-800">
								Mit der Registrierung akzeptieren Sie unsere{' '}
								<a
									href="https://www.b2b-hero.com/agb"
									target="_blank"
									rel="noopener noreferrer"
									className="text-primary-600 hover:text-primary-700 underline"
								>
									Nutzungsbedingungen
								</a>
								,{' '}
								<a
									href="https://www.b2b-hero.com/datenschutz"
									target="_blank"
									rel="noopener noreferrer"
									className="text-primary-600 hover:text-primary-700 underline"
								>
									Datenschutzerklärung
								</a>{' '}
								und stimmen dem Erhalt von Infos zu Produktupdates zu.
							</span>
						}
					/>
				</div>

				<Button variant="solid" type="submit" size="md" fullWidth>
					Kostenlos starten
				</Button>
			</form>

			<div className="text-center mt-6">
				<span className="text-sm text-bgBase-800">Bereits ein Konto? </span>
				<Link to="/login" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
					Hier anmelden
				</Link>
			</div>
		</Card>
	);

	const renderStep2 = () => (
		<Card className="p-8 max-w-lg mx-4">
			<ProgressBar step={2} />

			<div className="text-left mb-5">
				<h1 className="text-lg/8 font-bold text-text mb-2">Gleich geht&apos;s los</h1>
				<p className="text-text">Noch ein Schritt bis zu Ihren ersten Leads</p>
			</div>

			<div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-text mb-6">
				<span className="flex items-center">
					<BsCheck2 className="w-5 h-5 text-success-500 mr-1.5 flex-shrink-0" />
					Keine Kreditkarte
				</span>
				<span className="flex items-center">
					<BsCheck2 className="w-5 h-5 text-success-500 mr-1.5 flex-shrink-0" />
					50 Kontakte gratis
				</span>
			</div>

			{error && (
				<div className="bg-red-50 border border-red-200 text-warning-700 px-4 py-3 rounded-lg mb-4">
					{error}
				</div>
			)}
			{success && (
				<div className="text-lg bg-green-50 border-2 border-green-600 text-success-700 px-4 py-3 rounded-lg mb-4">
					{success}
				</div>
			)}

			{showLoginOption && (
				<div className="bg-yellow-50 border border-yellow-200 text-caution-700 px-4 py-3 rounded-lg mb-4">
					<p className="mb-2 text-sm">Gehe zum Login</p>
					<Button
						variant="solid"
						size="sm"
						onClick={() => navigate('/login', { state: { email: formData.email } })}
					>
						Zum Login
					</Button>
				</div>
			)}

			<form onSubmit={handleCompleteRegistration}>
				<LabeledInput
					id="email-confirm"
					label="E-Mail-Adresse"
					required
					type="email"
					autoComplete="email"
					value={formData.email}
					disabled
					className="mb-4"
				/>

				<div className="grid grid-cols-2 gap-3 mb-4">
					<LabeledInput
						id="firstName"
						label="Vorname"
						required
						name="firstName"
						autoComplete="given-name"
						placeholder="Max"
						value={formData.firstName}
						onChange={handleChange}
						disabled={loading}
					/>
					<LabeledInput
						id="lastName"
						label="Nachname"
						required
						name="lastName"
						autoComplete="family-name"
						placeholder="Mustermann"
						value={formData.lastName}
						onChange={handleChange}
						disabled={loading}
					/>
				</div>

				<LabeledInput
					id="companyName"
					label="Firmenname"
					required
					name="companyName"
					autoComplete="organization"
					placeholder="Muster GmbH"
					value={formData.companyName}
					onChange={handleChange}
					disabled={loading}
					className="mb-4"
				/>

				<LabeledInput
					id="password"
					label="Passwort"
					required
					hint="Mindestens 6 Zeichen"
					type={showPassword ? 'text' : 'password'}
					name="password"
					autoComplete="new-password"
					value={formData.password}
					onChange={handleChange}
					disabled={loading}
					minLength={6}
					className="mb-6"
					rightSlot={
						<button
							type="button"
							onClick={() => setShowPassword((prev) => !prev)}
							disabled={loading}
							aria-label={showPassword ? 'Passwort verbergen' : 'Passwort anzeigen'}
							className="absolute inset-y-0 right-0 flex items-center pr-4 text-bgBase-800 hover:text-text focus:outline-none disabled:cursor-not-allowed"
						>
							{showPassword ? <BsEyeSlash className="w-5 h-5" /> : <BsEye className="w-5 h-5" />}
						</button>
					}
				/>

				<div className="flex items-center gap-2">
					<Button type="button" onClick={() => setStep(1)} variant="outline" size="md" disabled={loading}>
						Zurück
					</Button>
					<Button
						type="submit"
						variant="solid"
						size="md"
						className="flex-1"
						loading={loading}
						disabled={Boolean(success)}
					>
						Jetzt loslegen
					</Button>
				</div>
			</form>
		</Card>
	);

	return (
		<div
			className="min-h-screen bg-cover bg-center bg-no-repeat flex items-center justify-center relative"
			style={{
				backgroundImage: `url(${registerBackground})`,
			}}
		>
			{/* Overlay with blur effect */}
			<div className="absolute inset-0 bg-black bg-opacity-30 backdrop-blur-sm" />

			<div className="relative z-10">
				<div className={step === 1 ? '' : 'hidden'}>{renderStep1()}</div>
				<div className={step === 2 ? '' : 'hidden'}>{renderStep2()}</div>
			</div>
		</div>
	);
};

export default RegisterPage;
